import { useLayoutEffect, useRef, useState, useMemo } from "react";
import { PMPagination } from "../../utils/PMPagination";
import { Link, useNavigate } from "react-router-dom";
import { LoadingRows, ItemStatus, dateToString } from "../../Utils";
import { ResourceEmpty } from "../../utils/ResourceEmpty";
import { Notes } from "../notes/notes";
import { 
  PencilIcon,
  BuildingOffice2Icon,
  UserIcon,
  CheckCircleIcon,
  DocumentTextIcon,
  ClockIcon,
  MapPinIcon,
  CalendarIcon
} from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";
import {
  orderProjectsUnderParents,
  projectParentDepth,
} from "../../../utils/projectListHierarchy";

export const ProjectsTable = (props) => {
  const { itemState, onPageChange, itemsCount, page, perPage, currentCount, onItemsSelect } =
    props;
  const { data, isLoading, errors } = itemState;
  const { t } = useTranslation();
  const navigate = useNavigate();

  const checkbox = useRef();
  const [checked, setChecked] = useState(false);
  const [indeterminate, setIndeterminate] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);

  const orderedRows = useMemo(
    () => orderProjectsUnderParents(data || []),
    [data]
  );

  useLayoutEffect(() => {
    const isIndeterminate =
      selectedItems?.length > 0 && selectedItems?.length < orderedRows?.length;
    setChecked(selectedItems?.length === orderedRows?.length);
    setIndeterminate(isIndeterminate);
    if (checkbox.current) {
      checkbox.current.indeterminate = isIndeterminate;
    }
    onItemsSelect(selectedItems);
  }, [selectedItems, orderedRows?.length, onItemsSelect]);

  function toggleAll() {
    setSelectedItems(checked || indeterminate ? [] : orderedRows);
    setChecked(!checked && !indeterminate);
    setIndeterminate(false);
  }

  const ProjectCard = ({ item, nestDepthPx }) => {
    return (
      <div 
        className={`shadow sm:overflow-hidden sm:rounded-md hover:shadow-lg transition-shadow duration-200 ${
          selectedItems.includes(item) ? "ring-2 ring-blue-400" : ""
        }`}
        style={
          nestDepthPx > 0
            ? {
                marginLeft: nestDepthPx,
                borderLeft: "3px solid rgb(226 232 240)",
              }
            : undefined
        }
      >
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div 
                className="flex-shrink-0 cursor-pointer hover:scale-105 transition-transform duration-200"
                onClick={() => navigate("/dashboard/projects/" + item?.id)}
              >
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <DocumentTextIcon className="w-6 h-6 text-blue-600" />
                </div>
              </div>
              <div className="ml-4">
                <div className="flex flex-wrap items-center gap-2">
                  <h3 
                    className="text-lg font-semibold text-gray-900 cursor-pointer hover:text-blue-600 transition-colors duration-200"
                    onClick={() => navigate("/dashboard/projects/" + item?.id)}
                  >
                    {item?.project_number || item?.name || "Missing Title"}
                  </h3>
                  {item?.is_offer ? (
                    <span className="inline-flex rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-semibold text-amber-900">
                      {t("project_list.badge.offer")}
                    </span>
                  ) : null}
                  {item?.parent_project ? (
                    <span className="inline-flex rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-700">
                      {t("project_list.badge.subproject")}
                    </span>
                  ) : null}
                  <ItemStatus item={item} />
                </div>
                {item?.parent_project ? (
                  <p className="text-xs text-slate-600 mt-1">
                    {t("project_list.sub_of")}:{" "}
                    <span className="font-medium text-slate-800">
                      {item.parent_project.displayTitle ||
                        item.parent_project.name ||
                        item.parent_project.project_number}
                    </span>
                  </p>
                ) : null}
                <p className="text-sm text-gray-600">{item?.name}</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              {item?.customer && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  <BuildingOffice2Icon className="w-3 h-3 mr-1" />
                  {item.customer.displayTitle || item.customer.name}
                </span>
              )}
            </div>
          </div>
        </div>
        
        <div className="bg-white px-6 py-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Customer Information */}
            {item?.customer && (
              <div>
                <dt className="text-sm font-medium text-gray-500 flex items-center">
                  <BuildingOffice2Icon className="w-4 h-4 mr-1" />
                  {t("Customer")}
                </dt>
                <dd className="mt-1 text-sm text-gray-900">
                  <Link
                    to={"/dashboard/customers/" + item?.customer?.id}
                    className="text-blue-400 hover:text-blue-600"
                  >
                    {item.customer.displayTitle || item.customer.name || "-"}
                  </Link>
                </dd>
              </div>
            )}

            {/* Delivery Address */}
            {item?.delivery_address && (
              <div>
                <dt className="text-sm font-medium text-gray-500 flex items-center">
                  <MapPinIcon className="w-4 h-4 mr-1" />
                  {t("Delivery Address")}
                </dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {item.delivery_address.displayTitle || "-"}
                </dd>
              </div>
            )}

            {/* Executing Employee */}
            {item?.executing_employee && (
              <div>
                <dt className="text-sm font-medium text-gray-500 flex items-center">
                  <UserIcon className="w-4 h-4 mr-1" />
                  {t("Executing Employee")}
                </dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {item.executing_employee.displayTitle || item.executing_employee.name || "-"}
                </dd>
              </div>
            )}

            {/* Agreement Date */}
            {item?.agreement_date && (
              <div>
                <dt className="text-sm font-medium text-gray-500 flex items-center">
                  <CalendarIcon className="w-4 h-4 mr-1" />
                  {t("Agreement Date")}
                </dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {dateToString(item.agreement_date)}
                </dd>
              </div>
            )}

            {/* Project Category */}
            {item?.project_category && (
              <div>
                <dt className="text-sm font-medium text-gray-500 flex items-center">
                  <CheckCircleIcon className="w-4 h-4 mr-1" />
                  {t("Category")}
                </dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {item.project_category.name || "-"}
                </dd>
              </div>
            )}

            {/* Created Date */}
            {item?.created_at && (
              <div>
                <dt className="text-sm font-medium text-gray-500 flex items-center">
                  <ClockIcon className="w-4 h-4 mr-1" />
                  {t("Created")}
                </dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {dateToString(item.created_at)}
                </dd>
              </div>
            )}
          </div>

          {/* Description */}
          {item?.description && (
            <div className="mt-6">
              <dt className="text-sm font-medium text-gray-500">{t("Description")}</dt>
              <dd 
                className="mt-1 text-sm text-gray-900 line-clamp-3"
                dangerouslySetInnerHTML={{ __html: item.description }}
              />
            </div>
          )}

          {/* Quick Actions */}
          <div className="mt-6 flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-gray-300 text-blue-400 focus:ring-blue-400"
                value={item.id}
                checked={selectedItems.includes(item)}
                onChange={(e) => {
                  setSelectedItems(
                    e.target.checked
                      ? [...selectedItems, item]
                      : selectedItems.filter((p) => p !== item)
                  );
                }}
              />
              <span className="text-sm text-gray-500">Select</span>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => navigate("/dashboard/projects/" + item.id)}
                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <PencilIcon className="h-4 w-4 mr-1" />
                Edit
              </button>
              <Notes module="project" module_param={item.id} />
            </div>
          </div>
        </div>
      </div>
    );
  };

  const Stats = () => {
    return (
      <>
        {orderedRows && orderedRows.length > 0 && (
          <div className="space-y-4 mt-5">
            {orderedRows.map((item) => (
              <ProjectCard
                key={item?.id}
                item={item}
                nestDepthPx={projectParentDepth(item, orderedRows) * 24}
              />
            ))}
          </div>
        )}
      </>
    );
  };

  return (
    <div className="divide-y divide-gray-200 overflow-hidden rounded-lg sm:gap-px sm:divide-y-0">
      {/* Selection bar */}
      {selectedItems?.length > 0 && (
        <div className="relative px-5 top-0 flex h-12 items-center space-x-3 bg-blue-400 rounded-xl mb-4 text-white">
          <div className="flex w-full items-center justify-between">
            <div className="">
              <span>{selectedItems.length} selected</span>
            </div>
            <div className="px-2 py-2">
              <button
                type="button"
                className="inline-flex items-center rounded bg-white px-2 py-1 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-white"
              >
                Edit
              </button>
              <button
                type="button"
                className="inline-flex items-center rounded bg-white px-2 py-1 ml-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-white"
              >
                Finish
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Select all checkbox */}
      <div className="mb-4">
        <div className="flex items-center">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-gray-300 text-blue-400 focus:ring-blue-400"
            ref={checkbox}
            checked={checked}
            onChange={toggleAll}
          />
          <label className="ml-2 text-sm font-medium text-gray-700">
            Select all
          </label>
        </div>
      </div>

      <div className="shadow sm:overflow-hidden sm:rounded-md">
        <div className="space-y-6 bg-white py-6 px-4 sm:p-6">
          {isLoading ? (
            <>
              <div className="bg-white border rounded-lg shadow-sm p-6 animate-pulse">
                <div className="flex items-center space-x-3">
                  <div className="h-4 w-4 bg-gray-200 rounded"></div>
                  <div className="flex-1">
                    <div className="h-4 bg-gray-200 rounded w-1/4 mb-2"></div>
                    <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
                    <div className="h-3 bg-gray-200 rounded w-1/3"></div>
                  </div>
                  <div className="flex space-x-2">
                    <div className="h-8 w-16 bg-gray-200 rounded"></div>
                    <div className="h-8 w-8 bg-gray-200 rounded"></div>
                  </div>
                </div>
              </div>
              <div className="bg-white border rounded-lg shadow-sm p-6 animate-pulse">
                <div className="flex items-center space-x-3">
                  <div className="h-4 w-4 bg-gray-200 rounded"></div>
                  <div className="flex-1">
                    <div className="h-4 bg-gray-200 rounded w-1/4 mb-2"></div>
                    <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
                    <div className="h-3 bg-gray-200 rounded w-1/3"></div>
                  </div>
                  <div className="flex space-x-2">
                    <div className="h-8 w-16 bg-gray-200 rounded"></div>
                    <div className="h-8 w-8 bg-gray-200 rounded"></div>
                  </div>
                </div>
              </div>
            </>
          ) : errors ? (
            <div className="text-center py-8 text-red-500">
              <div className="text-lg">Could not load projects</div>
            </div>
          ) : data && data.length > 0 ? (
            <Stats />
          ) : (
            <div className="text-center py-8 text-gray-500">
              <div className="text-lg">No projects found</div>
              <div className="text-sm">Create a new project</div>
            </div>
          )}
        </div>
      </div>

      <PMPagination
        disabled={isLoading}
        onPageChange={onPageChange}
        page={page}
        perPage={perPage}
        rowsCount={itemsCount}
        totalCount={itemsCount}
        currentCount={currentCount}
        showCount={true}
      />
    </div>
  );
};

ProjectsTable.defaultProps = {
  page: 1,
  itemState: [],
  itemsCount: 0,
  selectedItems: [],
  sort: "desc",
  sortBy: "created_at",
  onItemsSelect: () => {},
};
